import { siteMetadata } from 'config'
import { fetchJSON } from './fetch'

const { apiHost } = siteMetadata

const pollInterval = 1000 // milliseconds; 1 second
const jobTimeout = 600000 // milliseconds; 10 minutes
const failedFetchLimit = 5

const pollJob = async (jobId, onProgress = null) => {
  let time = 0
  let failedRequests = 0

  let json = null

  while (time < jobTimeout && failedRequests < failedFetchLimit) {
    try {
      /* eslint-disable-next-line no-await-in-loop */
      json = await fetchJSON(
        `${apiHost}/api/v1/internal/downloads/status/${jobId}`,
        'GET',
        {
          cache: 'no-cache',
        }
      )
    } catch (ex) {
      failedRequests += 1

      // sleep and try again
      /* eslint-disable-next-line no-await-in-loop */
      await new Promise((r) => {
        setTimeout(r, pollInterval)
      })
      time += pollInterval
      /* eslint-disable-next-line no-continue */
      continue
    }

    if (json === null) {
      if (error) {
        return { error }
      }

      throw Error('Download job failed')
    }

    /* eslint-disable-next-line no-await-in-loop */
    const {
      status = null,
      progress = null,
      queue_position: queuePosition = null,
      elapsed_time: elapsedTime = null,
      message = null,
      detail: error = null, // error message
      path = null,
    } = json

    if (status === 'failed') {
      if (error) {
        return { error }
      }

      throw Error('Download job failed')
    }

    if (status === 'success') {
      return { url: `${apiHost}${path}` }
    }

    if (
      onProgress &&
      (status === 'queued' || status === 'in_progress' || progress !== null)
    ) {
      onProgress({
        status,
        inProgress: true,
        progress: progress || 0,
        queuePosition: queuePosition || 0,
        elapsedTime: elapsedTime || null,
        message,
      })
    }

    // sleep
    /* eslint-disable-next-line no-await-in-loop */
    await new Promise((r) => {
      setTimeout(r, pollInterval)
    })
    time += pollInterval
  }

  // if we got here, it meant that we hit a timeout error or a fetch error
  if (failedRequests) {
    return {
      error:
        'network errors were encountered while creating your download.  The server may be too busy or your network connection may be having problems.  Please try again in a few minutes.',
    }
  }

  if (time >= jobTimeout) {
    return {
      error:
        'timeout while creating your download.  The server may be too busy.  Please try again in a few minutes.',
    }
  }

  return {
    error:
      'unexpected errors prevented your download job from completing successfully.  Please try again.',
  }
}

/**
 * Create job to download results for this state/county/place, model, census_tracts/counties, compare area combination
 * @param {String} reportingUnits - one of census_tracts, counties
 * @param {String} compareArea - one of by_place, by_county_place, by_state, conus (depends on reportingUnits)
 * @param {String} areaType - one of states, counties, places
 * @param {String} areaId - state, county, or place ID
 * @param {String} modelId - model ID
 * @param {String} format - one of csv,gdb,gpkg
 * @param {String} interventionIds - comma-delimited list of interventionIds to include
 * @param {Function} onProgress - callback to call with progress messages, if applicable
 * @returns
 */
export const getDownloadURL = async (
  reportingUnits,
  compareArea,
  areaType,
  areaId,
  modelId,
  format,
  interventionIds = '',
  onProgress = null
) => {
  let url = `${apiHost}/api/v1/internal/downloads/${reportingUnits}/${compareArea}/${areaType}/${areaId}/${modelId}/${format}`
  if (interventionIds) {
    url = `${url}?intervention_ids=${interventionIds}`
  }

  if (onProgress) {
    onProgress({
      status: 'queued',
      inProgress: true,
      progress: 0,
    })
  }

  let response = null
  try {
    response = await fetchJSON(url, 'POST')
  } catch (ex) {
    console.error(ex)
    return { url: null, error: ex }
  }

  if (response === null) {
    return { url: null, error: 'download returned 404 error' }
  }

  const { status, job, path } = response

  // created download immediately, no need to poll job
  if (status === 'success' && path) {
    if (onProgress) {
      onProgress({
        status,
        inProgress: false,
        progress: 100,
      })
    }

    return { url: `${apiHost}${path}` }
  }

  const result = await pollJob(job, onProgress)
  return result
}
